import { useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Button, Spinner, Text } from '@belong/ui';
import type { PaymentAccountResourceModel } from 'api/models';
import type {
  GroupedBalancesInfoModel,
  MonthlyStatementDetailInfoModel,
} from 'api/models.v2';
import { format, endOfMonth, startOfMonth, addMonths } from 'date-fns';
import { sortBy } from 'es-toolkit/compat';
import { balancesDataMapper } from 'pages/Accounts/utils/balances-data-mapper';
import queryString from 'query-string';
import { fetchUserGroupBalancesByMonth } from 'store/redux/accounts/actions';
import { selectBalancesByMonth } from 'store/redux/accounts/selectors';
import { fetchPaymentMethods } from 'store/redux/user/actions';
import { selectPaymentMethods } from 'store/redux/user/selectors';
import ModalLayout from 'layouts/ModalLayout/ModalLayout';
import { PAY_NOW_STRINGS } from './pay-now-strings';
import { PayNowContent } from './pay-now-content';
import ModalV2, { MODAL_TYPES } from 'components/Modal/ModalV2/ModalV2';

function extractPayableBalances(
  balances: MonthlyStatementDetailInfoModel[] | null,
): GroupedBalancesInfoModel[] {
  if (!balances) return [];

  const payable: GroupedBalancesInfoModel[] = [];

  for (const month of balances) {
    for (const home of month.homesDetails ?? []) {
      const { expenses, all } = home.groupedBalancesPerCategory || {};
      const balancesToCheck =
        all?.groupedBalancesInfo || expenses?.groupedBalancesInfo || [];

      for (const balance of balancesToCheck) {
        if (balance.canBePaid && balance.category === 'Expenses') {
          payable.push(balance);
        }
      }
    }
  }

  return payable;
}

function useFetchPayNowData() {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);

  useState(() => {
    const from = startOfMonth(new Date());
    const until = endOfMonth(addMonths(from, 1));

    const balancesQueryParams = queryString.stringify({
      from: `${format(from, 'yyyy-MM-dd')}T00:00:00.000Z`,
      until: `${format(until, 'yyyy-MM-dd')}T23:59:59.999Z`,
      groupBalanceType: 'Flat',
    });

    async function load() {
      try {
        await Promise.all([
          dispatch(fetchUserGroupBalancesByMonth(balancesQueryParams)),
          dispatch(fetchPaymentMethods()),
        ]);
      } finally {
        setLoading(false);
      }
    }

    load();
  });

  return loading;
}

type PayNowModalProps = {
  isOpen: boolean;
  onDismiss: () => void;
  onPaymentComplete: (result: {
    success: boolean;
    errorMessage?: string;
  }) => void;
  currentMonth?: string;
};

export function PayNowModal({
  isOpen,
  onDismiss,
  onPaymentComplete,
  currentMonth,
}: PayNowModalProps) {
  const loading = useFetchPayNowData();

  const rawBalances = useSelector(selectBalancesByMonth);
  const paymentMethodsRaw = useSelector(selectPaymentMethods);

  const balances = useMemo(
    () =>
      balancesDataMapper(sortBy(rawBalances ?? [], ['monthSummaryInfo.month'])),
    [rawBalances],
  );

  const payableBalances = useMemo(
    () => extractPayableBalances(balances),
    [balances],
  );

  const paymentMethods = useMemo(
    () => (paymentMethodsRaw ?? []) as PaymentAccountResourceModel[],
    [paymentMethodsRaw],
  );

  return (
    <ModalV2
      show={isOpen}
      onHide={onDismiss}
      type={MODAL_TYPES.LARGE}
      noFullPageHeader
      contentClassName="!pb-0"
    >
      <div className="pt-xl">
        <Text
          variant="h2"
          fontWeight="semibold"
          className="mb-xs px-sm text-center"
        >
          {currentMonth ? `${currentMonth} ` : ''}
          {PAY_NOW_STRINGS.title}
        </Text>
        <Text variant="body" className="mb-lg text-center">
          Choose to pay your balances right now with your desired payment
          method.
        </Text>
        {loading ? (
          <div className="flex items-center justify-center py-2xl">
            <Spinner />
          </div>
        ) : (
          <PayNowContent
            balances={payableBalances}
            paymentMethods={paymentMethods}
            isHomeowner={false}
            onPaymentComplete={onPaymentComplete}
            renderFooter={({ onPayNowClick, isDisabled, isLoading }) => (
              <ModalLayout.BottomBar
                fixedBottom
                rightNode={
                  <Button
                    onClick={onPayNowClick}
                    disabled={isDisabled || isLoading}
                    size={{ _: 'fluid', sm: 'default' }}
                  >
                    {PAY_NOW_STRINGS['pay_now.button']}
                  </Button>
                }
              />
            )}
          />
        )}
      </div>
    </ModalV2>
  );
}
